import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Mail, CheckSquare, FolderClosed, FileText, Mic, ArrowRight, Loader2,
  Sparkles, Calendar, Plus, Send, FileEdit, Workflow, Upload, Lightbulb,
  Globe, MessageCircle, Hexagon, Zap, ChevronRight, ScreenShare
} from "lucide-react";
import { aiQuick, api, notesApi, filesApi, eventsApi } from "../api";
import { useAuth } from "../AuthContext";
import { useWM } from "../store";
import { APPS } from "./index";
import { parseVoiceCommand, stopSpeaking, speak, probeMic, voiceErrorText, micBlockerText } from "../voice";

const QUICK_PROMPTS = [
  { id: "sum-emails", label: "Summarize my emails", prompt: "Summarize my unread emails in 3 bullets." },
  { id: "schedule", label: "What's on my schedule?", prompt: "What is on my schedule today?" },
  { id: "tasks", label: "Show my pending tasks", prompt: "List my pending tasks with priorities." },
  { id: "find-files", label: "Find files about project X", prompt: "Help me find files related to the most recent project." },
];

const QUICK_ACTIONS = [
  { id: "share-screen", label: "Share Screen", sub: "Capture & share", icon: ScreenShare, color: "#f97316", appId: "recorder" },
  { id: "new-task", label: "New Task", sub: "Create a task", icon: Plus, color: "#34d399", appId: "tasks" },
  { id: "send-email", label: "Send Email", sub: "Compose email", icon: Send, color: "#63a0ff", appId: "mail" },
  { id: "upload", label: "Upload File", sub: "From device", icon: Upload, color: "#ec4899", appId: "files" },
  { id: "take-note", label: "Take Note", sub: "Quick note", icon: FileText, color: "#3b82f6", appId: "notes" },
  { id: "workflow", label: "Start Workflow", sub: "Run with Novo", icon: Workflow, color: "#eab308", appId: "wingman" },
];

const TINTS = {
  emails: { bg: "linear-gradient(160deg, rgba(99,160,255,0.18), rgba(99,160,255,0.06))", border: "rgba(99,160,255,0.3)", text: "#dce8ff", lightBg: "linear-gradient(135deg,#eff6ff,#dbeafe)", lightText: "#1e40af", lightBorder: "rgba(59,130,246,0.3)" },
  tasks:  { bg: "linear-gradient(160deg, rgba(52,211,153,0.18), rgba(52,211,153,0.05))", border: "rgba(52,211,153,0.3)", text: "#d4f5e2", lightBg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", lightText: "#166534", lightBorder: "rgba(34,197,94,0.3)" },
  files:  { bg: "linear-gradient(160deg, rgba(167,139,250,0.18), rgba(167,139,250,0.05))", border: "rgba(167,139,250,0.3)", text: "#e6dcff", lightBg: "linear-gradient(135deg,#faf5ff,#ede9fe)", lightText: "#5b21b6", lightBorder: "rgba(139,92,246,0.3)" },
  notes:  { bg: "linear-gradient(160deg, rgba(59,130,246,0.18), rgba(59,130,246,0.05))", border: "rgba(59,130,246,0.3)", text: "#fbe6b4", lightBg: "linear-gradient(135deg,#fffbeb,#fef3c7)", lightText: "#92400e", lightBorder: "rgba(234,179,8,0.3)" },
};
const ACCENTS = { emails: "#63a0ff", tasks: "#34d399", files: "#a78bfa", notes: "#3b82f6" };

function greet() {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const t = new Date();
  return d.toDateString() === t.toDateString();
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const y = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function StatCard({ id, label, value, sub, tint, accent, icon: Icon, onClick }) {
  // The Dashboard always renders inside the light `.hexa-light-app` pane, while
  // the OS shell itself stays data-theme="dark". Reading the root theme here
  // therefore always returned false, so the cards kept their dark gradients and
  // near-white inline text on a light surface — unreadable. Use the light set.
  const isLight = true;
  return (
    <button
      data-testid={`stat-card-${id}`}
      onClick={onClick}
      className="text-left relative overflow-hidden p-4 transition-all hover:-translate-y-0.5"
      style={{
        background: isLight ? tint.lightBg : tint.bg,
        border: `1px solid ${isLight ? tint.lightBorder : tint.border}`,
        clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
      }}
    >
      <div className="flex items-center gap-2 mb-2 relative z-[1]">
        <span
          className="inline-flex items-center justify-center"
          style={{
            width: 22, height: 24,
            clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            background: `${accent}33`,
          }}
        >
          <Icon size={11} color={accent} />
        </span>
        <span style={{ color: accent }} className="mono uppercase tracking-widest text-[9px] font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-light tracking-tight mono mb-0.5" style={{ color: isLight ? tint.lightText : tint.text }}>{value}</div>
      <div className="text-[10px] mono uppercase tracking-widest" style={{ color: isLight ? `${tint.lightText}99` : "#c7c1ff60" }}>{sub}</div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { open: openApp } = useWM();

  const [stats, setStats] = useState({ emails: 0, tasks: 0, files: 0, notes: 0 });
  const [todayTasks, setTodayTasks] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [q, setQ] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [reasonMode, setReasonMode] = useState(false);
  const [deepSearch, setDeepSearch] = useState(false);

  const open = (id, props) => {
    const app = APPS.find(a => a.id === id);
    if (app) openApp(app, props);
  };

  const spokenRef = useRef(false);
  const load = useCallback(async () => {
    if (!user) return;
    let unreadEmails = 0;
    let pendingTasks = 0;
    let meetingsToday = 0;
    try {
      const msgs = await api.get(`/mail/messages?account=${encodeURIComponent(user.address)}&folder=inbox`).then(r => r.data);
      unreadEmails = msgs.filter(m => !m.read).length;
      setStats(s => ({ ...s, emails: unreadEmails }));
    } catch { /* ignore */ }

    try {
      const tasks = await api.get("/tasks").then(r => r.data);
      const pending = tasks.filter(t => t.status !== "done");
      pendingTasks = pending.length;
      setStats(s => ({ ...s, tasks: pendingTasks }));
      setTodayTasks(pending.slice(0, 4));
    } catch { /* ignore */ }

    try {
      const files = await filesApi.all();
      setStats(s => ({ ...s, files: files.length }));
    } catch { /* ignore */ }

    try {
      const notes = await notesApi.list();
      setStats(s => ({ ...s, notes: notes.length }));
    } catch { /* ignore */ }

    try {
      const events = await eventsApi.list();
      const sorted = events
        .filter(e => e.date)
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .slice(0, 4);
      setSchedule(sorted);
      const today = new Date().toISOString().slice(0, 10);
      meetingsToday = events.filter((e) => (e.date || "").startsWith(today)).length;
    } catch { /* ignore */ }

    // Recent AI conversations (chat sessions)
    try {
      const r = await api.get("/ai/chat/sessions").then(x => x.data);
      setConversations((r || []).slice(0, 4));
    } catch { /* ignore */ }

    // Voice-greet the user — once per mount, after stats finish loading.
    if (!spokenRef.current) {
      spokenRef.current = true;
      const first = (user.name || user.address || "").split(/[ @]/)[0] || "there";
      const cap = first.charAt(0).toUpperCase() + first.slice(1);
      const fmt = (n, s, p) => `${n} ${n === 1 ? s : p}`;
      const line = `${greet()}, ${cap}. You have ${fmt(pendingTasks, "task", "tasks")}, ${fmt(meetingsToday, "meeting", "meetings")}, and ${fmt(unreadEmails, "unread email", "unread emails")}.`;
      try { speak(line); } catch { /* speech unsupported */ }
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const ask = async (override) => {
    const prompt = (override || q).trim();
    if (!prompt) return;

    // 1. Voice-style command? "open email", "what's my schedule today", "new task"...
    //    Route navigation/actions to the OS instead of treating them as AI questions.
    const cmd = parseVoiceCommand(prompt);
    if (cmd && cmd.action === "open" && cmd.appId) {
      open(cmd.appId);
      setQ("");
      return;
    }
    if (cmd && cmd.action === "stop") {
      stopSpeaking();
      setQ("");
      return;
    }

    // 2. Reason / Deep Search mode → route to Novo, otherwise quick AI
    if (reasonMode || deepSearch) {
      open("wingman", { initialGoal: prompt });
      setQ("");
      return;
    }
    setAskBusy(true);
    try {
      const r = await aiQuick(prompt, "You are HEXA. Answer concisely. If asked to plan, use bullets.");
      // open chat with this seeded question for richer follow-up
      open("chat", { seedQuestion: prompt, seedAnswer: r.text });
    } catch { /* ignore */ }
    setAskBusy(false);
    setQ("");
  };

  const startListening = async () => {
    setVoiceError("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceError("Voice input requires Chrome, Edge, or Safari. Your current browser doesn't support speech recognition.");
      return;
    }
    if (!window.isSecureContext) {
      setVoiceError("Voice input only works on HTTPS. The current page is not secure.");
      return;
    }
    // Pre-check mic permission so we can surface a useful message instead of failing silently.
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: "microphone" });
        if (status.state === "denied") {
          setVoiceError("Microphone is blocked. Click the lock icon next to the URL → Site settings → allow Microphone, then reload.");
          return;
        }
      }
    } catch { /* permission API not supported — continue */ }

    setVoiceError("Checking microphone…");
    const mic = await probeMic();
    const blocker = micBlockerText(mic.verdict);
    if (blocker) { setVoiceError(blocker); return; }
    setVoiceError("");
    const deadStream = mic.verdict === "silent";
    let retriedNoSpeech = false;

    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";
      if (code === "no-speech" && !retriedNoSpeech) {
        retriedNoSpeech = true;
        setVoiceError("Still listening — speak now…");
        try { r.start(); return; } catch (_) { /* fall through */ }
      }
      setListening(false);
      if (code !== "aborted") setVoiceError(voiceErrorText(code, { deadStream }));
    };
    r.onresult = (ev) => {
      let t = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) t += ev.results[i][0].transcript;
      setQ(t);
      if (ev.results[ev.results.length - 1].isFinal) setTimeout(() => ask(t), 200);
    };
    try {
      r.start();
    } catch (e) {
      setListening(false);
      setVoiceError("Could not start microphone. Close other tabs using the mic and try again.");
    }
  };

  const firstName = (user?.name || user?.username || "there").split(" ")[0];

  return (
    <div className="h-full overflow-y-auto" style={{ background: "#ffffff" }}>
      <div className="grid lg:grid-cols-[1fr,340px] gap-4 sm:gap-6 p-3 sm:p-6 max-w-[1500px] mx-auto">
        {/* MAIN COLUMN */}
        <div className="space-y-6 min-w-0">
          {/* Greeting */}
          <div className="text-center pt-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              <span
                className="font-medium"
                style={{
                  background: "linear-gradient(90deg, #a78bfa, #a78bfa)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {greet()}, {firstName}
              </span>
              <span className="ml-2">👋</span>
            </h1>
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const meetingsToday = (schedule || []).filter((e) => (e.date || "").startsWith(today)).length;
              const t = stats.tasks || 0;
              const e = stats.emails || 0;
              const fmt = (n, sing, plur) => `${n} ${n === 1 ? sing : plur}`;
              return (
                <div
                  data-testid="dashboard-greeting-stats"
                  className="text-sm text-white/70 mt-1.5 leading-relaxed"
                >
                  You have{" "}
                  <button
                    onClick={() => open("tasks")}
                    className="font-medium text-[#34d399] hover:underline"
                    data-testid="greeting-stat-tasks"
                  >
                    {fmt(t, "task", "tasks")}
                  </button>
                  {", "}
                  <button
                    onClick={() => open("meetings")}
                    className="font-medium text-[#60a5fa] hover:underline"
                    data-testid="greeting-stat-meetings"
                  >
                    {fmt(meetingsToday, "meeting", "meetings")}
                  </button>
                  {", and "}
                  <button
                    onClick={() => open("mail")}
                    className="font-medium text-[#a78bfa] hover:underline"
                    data-testid="greeting-stat-emails"
                  >
                    {fmt(e, "unread email", "unread emails")}
                  </button>
                  {"."}
                </div>
              );
            })()}
            <div className="mono text-[10px] uppercase tracking-[0.4em] text-[#c7c1ff]/55 mt-2">// how can i help you today?</div>
          </div>

          {/* AI Command Center */}
          <div
            className="panel p-4"
            style={{
              background: "linear-gradient(180deg, rgba(139,92,246,0.06), rgba(167,139,250,0.04))",
              clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))",
            }}
          >
            <input
              data-testid="dashboard-ask-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder="Ask HEXA anything…"
              className="w-full bg-transparent outline-none px-2 py-2 text-base placeholder-[#c7c1ff]/45"
            />

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                data-testid="dashboard-mode-reason"
                onClick={() => setReasonMode(m => !m)}
                className="flex items-center gap-1.5 px-3 py-1.5 mono text-[10px] uppercase tracking-widest"
                style={{
                  background: reasonMode ? "rgba(59,130,246,0.18)" : "rgba(168,227,239,0.06)",
                  border: `1px solid ${reasonMode ? "#3b82f6" : "rgba(168,227,239,0.18)"}55`,
                  color: reasonMode ? "#3b82f6" : "#c7c1ff",
                }}
              >
                <Lightbulb size={12} /> Reason
              </button>
              <button
                data-testid="dashboard-mode-deepsearch"
                onClick={() => setDeepSearch(m => !m)}
                className="flex items-center gap-1.5 px-3 py-1.5 mono text-[10px] uppercase tracking-widest"
                style={{
                  background: deepSearch ? "rgba(139,92,246,0.18)" : "rgba(168,227,239,0.06)",
                  border: `1px solid ${deepSearch ? "#8b5cf6" : "rgba(168,227,239,0.18)"}55`,
                  color: deepSearch ? "#8b5cf6" : "#c7c1ff",
                }}
              >
                <Globe size={12} /> Deep Search
              </button>
              <span className="mono text-[9px] text-[#c7c1ff]/40 uppercase tracking-widest">
                {(reasonMode || deepSearch) ? "// will deploy novo" : "// quick answer"}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  data-testid="dashboard-ask-mic"
                  onClick={startListening}
                  className={`p-2 ${listening ? "text-[#3b82f6] pulse-amber" : "text-[#c7c1ff]/65 hover:text-[#8b5cf6]"}`}
                  title="Voice input"
                >
                  <Mic size={16} />
                </button>
                <button
                  data-testid="dashboard-ask-submit"
                  onClick={() => ask()}
                  disabled={askBusy || !q.trim()}
                  className="p-2.5 disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #a78bfa, #a78bfa)", color: "#0b1014" }}
                >
                  {askBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                </button>
              </div>
            </div>
            {voiceError && (
              <div
                data-testid="dashboard-voice-error"
                className="mt-2 px-3 py-2 mono text-[10px] uppercase tracking-widest border border-[#f87171]/40 bg-[#f87171]/10 text-[#fca5a5] flex items-start gap-2"
              >
                <span className="text-[#f87171] shrink-0">// mic</span>
                <span className="flex-1 normal-case tracking-normal">{voiceError}</span>
                <button
                  onClick={() => setVoiceError("")}
                  className="text-[#fca5a5]/60 hover:text-[#fca5a5] shrink-0"
                  aria-label="dismiss"
                >×</button>
              </div>
            )}
          </div>

          {/* Quick prompts */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {QUICK_PROMPTS.map(qp => (
              <button
                key={qp.id}
                data-testid={`quick-${qp.id}`}
                onClick={() => ask(qp.prompt)}
                className="px-3 py-2.5 mono text-[10px] uppercase tracking-widest text-left flex items-center gap-2 hover:border-[#8b5cf6]/60 transition-colors"
                style={{
                  background: "rgba(139,92,246,0.04)",
                  border: "1px solid rgba(139,92,246,0.16)",
                  color: "#c7c1ff",
                }}
              >
                <Sparkles size={11} color="#8b5cf6" />
                <span className="truncate">{qp.label}</span>
              </button>
            ))}
          </div>

          {/* Recent Conversations */}
          <div className="panel p-4" style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}>
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle size={13} color="#a78bfa" />
              <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#a78bfa]">// recent conversations</div>
              <button onClick={() => open("chat")} className="ml-auto mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/55 hover:text-[#8b5cf6]" data-testid="view-all-conversations">view all →</button>
            </div>
            {conversations.length === 0 ? (
              <div className="mono text-[10px] text-[#c7c1ff]/45 uppercase tracking-widest py-3">// no conversations yet · ask hexa above to start</div>
            ) : (
              <div className="divide-y divide-[#8b5cf6]/10">
                {conversations.map(c => (
                  <button
                    key={c.id}
                    data-testid={`conversation-${c.id}`}
                    onClick={() => open("chat", { sessionId: c.id })}
                    className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-[#8b5cf6]/5 px-2"
                  >
                    <MessageCircle size={11} color="#c7c1ff" className="opacity-50" />
                    <div className="text-sm text-[#e8f4f8]/90 flex-1 truncate">{c.title || c.preview || "Untitled chat"}</div>
                    <div className="mono text-[10px] text-[#c7c1ff]/45 uppercase tracking-widest whitespace-nowrap">{timeAgo(c.updated_at || c.created)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {QUICK_ACTIONS.map(qa => {
              const Icon = qa.icon;
              return (
                <button
                  key={qa.id}
                  data-testid={`action-${qa.id}`}
                  onClick={() => open(qa.appId)}
                  className="p-4 flex flex-col items-start gap-2 hover:-translate-y-0.5 transition-all"
                  style={{
                    background: `linear-gradient(160deg, ${qa.color}22, ${qa.color}06)`,
                    border: `1px solid ${qa.color}44`,
                    clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
                  }}
                >
                  <span
                    className="inline-flex items-center justify-center"
                    style={{
                      width: 32, height: 36,
                      clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                      background: `${qa.color}33`,
                    }}
                  >
                    <Icon size={14} color={qa.color} />
                  </span>
                  <div className="text-sm font-medium" style={{ color: qa.color }}>{qa.label}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/60">{qa.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-5 min-w-0">
          {/* Overview */}
          <div>
            <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#c7c1ff]/75 mb-3">Overview</div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard id="emails" label="Emails" value={stats.emails} sub="Unread" tint={TINTS.emails} accent={ACCENTS.emails} icon={Mail} onClick={() => open("mail")} />
              <StatCard id="tasks" label="Tasks" value={stats.tasks} sub="Pending" tint={TINTS.tasks} accent={ACCENTS.tasks} icon={CheckSquare} onClick={() => open("tasks")} />
              <StatCard id="files" label="Files" value={stats.files} sub="Total" tint={TINTS.files} accent={ACCENTS.files} icon={FolderClosed} onClick={() => open("files")} />
              <StatCard id="notes" label="Notes" value={stats.notes} sub="Total" tint={TINTS.notes} accent={ACCENTS.notes} icon={FileText} onClick={() => open("notes")} />
            </div>
          </div>

          {/* Today's Tasks */}
          <div className="panel p-4" style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}>
            <div className="flex items-center gap-2 mb-3">
              <CheckSquare size={13} color="#34d399" />
              <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#34d399]">today's tasks</div>
              <button onClick={() => open("tasks")} className="ml-auto mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/55 hover:text-[#8b5cf6]">view all</button>
            </div>
            {todayTasks.length === 0 ? (
              <div className="mono text-[10px] text-[#c7c1ff]/45 uppercase tracking-widest py-2">// no tasks</div>
            ) : (
              <div className="space-y-2">
                {todayTasks.map(t => (
                  <button
                    key={t.id}
                    data-testid={`today-task-${t.id}`}
                    onClick={() => open("tasks")}
                    className="w-full flex items-center gap-2 p-2 hover:bg-[#34d399]/5 text-left"
                  >
                    <span className={`w-3.5 h-3.5 border ${t.status === "done" ? "bg-[#34d399] border-[#34d399]" : "border-[#34d399]/60"} flex-shrink-0`} />
                    <div className="text-xs flex-1 truncate">{t.title}</div>
                    {t.due && <div className="mono text-[10px] text-[#a78bfa] whitespace-nowrap">{t.due}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="panel p-4" style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}>
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={13} color="#a78bfa" />
              <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#a78bfa]">upcoming events</div>
              <button onClick={() => open("calendar")} className="ml-auto mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/55 hover:text-[#8b5cf6]">view calendar</button>
            </div>
            {schedule.length === 0 ? (
              <div className="mono text-[10px] text-[#c7c1ff]/45 uppercase tracking-widest py-2">// nothing upcoming</div>
            ) : (
              <div className="space-y-2.5">
                {schedule.map(e => (
                  <button
                    key={e.id}
                    data-testid={`upcoming-${e.id}`}
                    onClick={() => open("calendar")}
                    className="w-full flex items-center gap-2.5 text-left hover:bg-[#a78bfa]/5 px-1 py-1"
                  >
                    <span
                      className="inline-flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 26, height: 30,
                        clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                        background: "rgba(167,139,250,0.18)",
                      }}
                    >
                      <Calendar size={11} color="#a78bfa" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">{e.title || "Untitled"}</div>
                      <div className="mono text-[10px] text-[#c7c1ff]/55">{e.date}{e.time ? `, ${e.time}` : ""}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* HEXA Novo card */}
          <div
            className="p-4 relative overflow-hidden cursor-pointer"
            onClick={() => open("wingman")}
            data-testid="wingman-card"
            style={{
              background: "linear-gradient(135deg, rgba(59,130,246,0.22), rgba(139,92,246,0.14))",
              border: "1px solid rgba(59,130,246,0.45)",
              clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center justify-center"
                style={{
                  width: 40, height: 46,
                  clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                  background: "linear-gradient(160deg, #3b82f6, #f59e0b)",
                  boxShadow: "0 0 18px rgba(59,130,246,0.55)",
                }}
              >
                <Zap size={18} color="#0b1014" />
              </span>
              <div className="flex-1">
                <div className="font-medium text-[#3b82f6]">HEXA Novo</div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// agentic ai · always on</div>
              </div>
              <ChevronRight size={16} color="#3b82f6" />
            </div>
            <div className="mt-3 text-[11px] text-[#e8f4f8]/85 leading-relaxed">
              Type a goal — Novo plans, calls tools, and delivers. <span className="text-[#3b82f6]">Try:</span> <span className="italic">"prep my morning"</span> or <span className="italic">"draft a launch post + follow-up task"</span>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
